import { type KeyboardEvent } from "react";

import { keepInputFocus } from "ui/inline-editable-field/inline-editable-field.helpers";
import { type InlineEditableFieldProps } from "ui/inline-editable-field/inline-editable-field.types";

type UseInlineEditableFieldProps = Required<
  Pick<InlineEditableFieldProps, "isEditing" | "isSaving" | "canSave">
> &
  Pick<InlineEditableFieldProps, "disabled" | "onSave" | "onCancel" | "onKeyDown">;

export const useInlineEditableField = ({
  isEditing,
  isSaving,
  canSave,
  disabled,
  onSave,
  onCancel,
  onKeyDown,
}: UseInlineEditableFieldProps) => {
  const isCancelDisabled = !!disabled || isSaving;
  const isSaveDisabled = isCancelDisabled || !canSave;

  // The caller's handler runs first, so it can claim a key with `preventDefault`.
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onKeyDown?.(event);

    if (!isEditing || event.defaultPrevented) return;

    if (event.key === "Enter" && !isSaveDisabled) {
      event.preventDefault();
      onSave();
    }

    if (event.key === "Escape" && !isCancelDisabled) {
      event.preventDefault();
      onCancel();
    }
  };

  return {
    isSaveDisabled,
    isCancelDisabled,
    handleKeyDown,
    handleActionMouseDown: keepInputFocus,
  };
};
